// Pflege "Finanzielle Unterstützung" (DCP-2228 follow-up, 14.09.2026)
//
// Vereinfachte Übertragung von PaymentNoteTemplateListPage.tsx: kein
// Verweis-Zähler beim Löschen (es gibt kein "wird von N Veranstaltungen
// benutzt"-Konzept für diese Einträge, anders als bei den Textvorlagen) —
// ansonsten wortgleicher Aufbau (dreischichtige Rollen-Prüfung, Mehrfachauswahl,
// Lösch-Bestätigung).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { ApiError } from '@/lib/api-client';
import { formatCurrency } from '@/lib/i18n/formatters';
import { cn } from '@/lib/utils';
import {
  useFinancialSupport,
  useSaveFinancialSupport,
  useDeleteFinancialSupport,
  useEventTypes,
} from '@/services/event-service';
import { formatDay } from '@/features/events/mailing-match';
import { StatusMark } from '@/components/ui/status-mark';
import { Role, type FinancialSupport, type SaveFinancialSupportRequest } from '@/types';
import { FinancialSupportFormDialog } from './FinancialSupportFormDialog';
import { pruneSelection, selectionState, toggleAll, toggleOne } from './selection';

export function FinancialSupportListPage() {
  const { t } = useTranslation(['settings', 'events']);
  const { toast } = useToast();

  const { data: me } = useMe();
  /**
   * Darf diese Person die Pflegeseite überhaupt benutzen? Am RECHT, nicht an der Rolle.
   *
   * ⚠️ Der Server prüft `requireCentral` auf denselben Routen — das hier blendet nur
   * aus, es sichert nichts ab.
   */
  const canManage = me?.role === Role.Zentral || me?.role === Role.Admin;

  const { data: entries = [], isLoading, isError, error } = useFinancialSupport();
  const { data: eventTypes = [] } = useEventTypes();
  const saveMutation = useSaveFinancialSupport();
  const deleteMutation = useDeleteFinancialSupport();

  const [selected, setSelected] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FinancialSupport | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);

  const ids = useMemo(() => entries.map((entry) => entry.id), [entries]);
  const liveSelection = useMemo(() => pruneSelection(selected, ids), [selected, ids]);
  const headerState = selectionState(entries.length, liveSelection.length);
  const isDenied = error instanceof ApiError && (error.status === 401 || error.status === 403);

  const vaTypeName = (id: string) => eventTypes.find((et) => et.id === id)?.name ?? id;
  const sortedVaTypeNames = (entry: FinancialSupport) =>
    entry.vaTypes.map(vaTypeName).sort((a, b) => a.localeCompare(b, 'de'));

  const openCreate = () => {
    setEditing(null);
    setSaveError(null);
    setDialogOpen(true);
  };

  const openEdit = (entry: FinancialSupport) => {
    setEditing(entry);
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSave = (req: SaveFinancialSupportRequest) => {
    setSaveError(null);
    saveMutation.mutate(req, {
      onSuccess: () => {
        toast({ title: t('financialSupport.toast.saved') });
        setDialogOpen(false);
      },
      onError: (e) => setSaveError(e instanceof Error ? e.message : String(e)),
    });
  };

  const handleDelete = (doomed: string[]) => {
    deleteMutation.mutate(doomed, {
      onSuccess: (result) => {
        setConfirmDelete(null);
        setSelected([]);

        toast({ title: t('financialSupport.toast.deleted', { count: result.removed.length }) });

        if (result.failed.length > 0) {
          toast({
            title: t('financialSupport.toast.deleteFailed'),
            description: result.failed.map((f) => `${f.id}: ${f.reason}`).join('\n'),
            variant: 'destructive',
          });
        }
      },
      onError: (e) =>
        toast({
          title: t('financialSupport.toast.error'),
          description: e instanceof Error ? e.message : String(e),
          variant: 'destructive',
        }),
    });
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">{t('financialSupport.page.title')}</h2>
        <p className="p-8 text-center text-muted-foreground">{t('financialSupport.page.noAccess')}</p>
      </div>
    );
  }

  const nameOf = (id: string) => entries.find((entry) => entry.id === id)?.name ?? id;

  const deleteDescription = (): string => {
    if (!confirmDelete) return '';

    return confirmDelete.length === 1
      ? t('financialSupport.delete.descriptionOne', { name: nameOf(confirmDelete[0]) })
      : t('financialSupport.delete.descriptionMany', { count: confirmDelete.length });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2 h-7 gap-1 px-2">
            <Link to="/settings">
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('financialSupport.page.backToSettings')}
            </Link>
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">{t('financialSupport.page.title')}</h2>
          <p className="text-muted-foreground">{t('financialSupport.page.description')}</p>
        </div>
        <Button className="gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          {t('financialSupport.actions.create')}
        </Button>
      </div>

      {isLoading ? (
        <p className="p-8 text-center text-muted-foreground">{t('financialSupport.page.loading')}</p>
      ) : isDenied ? (
        <p className="p-8 text-center text-muted-foreground">{t('financialSupport.page.noAccess')}</p>
      ) : isError ? (
        <p className="p-8 text-center text-red-600">{t('financialSupport.page.loadError')}</p>
      ) : entries.length === 0 ? (
        <p className="rounded-md border p-8 text-center text-muted-foreground">
          {t('financialSupport.page.empty')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={headerState === 'all'}
                  onCheckedChange={() => setSelected(toggleAll(ids, liveSelection))}
                  aria-label={t('financialSupport.table.selectAll')}
                  className={cn(headerState === 'some' && 'data-[state=checked]:bg-primary/50')}
                />
              </TableHead>
              <TableHead>{t('financialSupport.table.name')}</TableHead>
              <TableHead>{t('events:form.fields.audience')}</TableHead>
              <TableHead>{t('financialSupport.table.flatRateAmount')}</TableHead>
              <TableHead>{t('financialSupport.table.vaTypes')}</TableHead>
              <TableHead>{t('financialSupport.table.proofRequired')}</TableHead>
              <TableHead>{t('financialSupport.table.modified')}</TableHead>
              <TableHead className="text-right">{t('financialSupport.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((entry) => {
              const isSelected = liveSelection.includes(entry.id);

              return (
                <TableRow key={entry.id} className={cn(isSelected && 'bg-blue-50')}>
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => setSelected(toggleOne(liveSelection, entry.id))}
                      aria-label={t('financialSupport.table.selectRow', { name: entry.name })}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{entry.name}</TableCell>
                  <TableCell className="text-sm">{t(`events:audience.${entry.audience}`)}</TableCell>
                  <TableCell className="text-sm">{formatCurrency(entry.flatRateAmount, 'EUR')}</TableCell>
                  <TableCell className="max-w-[20rem] text-sm text-muted-foreground">
                    {sortedVaTypeNames(entry).map((name) => (
                      <div key={name}>{name}</div>
                    ))}
                  </TableCell>
                  <TableCell>
                    <StatusMark value={entry.proofRequired} label={t('financialSupport.table.proofRequired')} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDay(entry.modifiedAt)}
                    {entry.modifiedBy && <div className="truncate">{entry.modifiedBy}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(entry)}
                      aria-label={t('financialSupport.actions.edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete([entry.id])}
                      aria-label={t('financialSupport.actions.delete')}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {liveSelection.length > 0 && (
        <div className="sticky bottom-0 z-40 flex items-center justify-between gap-4 border-t bg-background/95 p-3 backdrop-blur">
          <span className="text-sm text-muted-foreground">
            {t('financialSupport.bulk.selected', { count: liveSelection.length })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected([])}>
              {t('financialSupport.bulk.clearSelection')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={() => setConfirmDelete([...liveSelection])}
            >
              <Trash2 className="h-4 w-4" />
              {t('financialSupport.bulk.deleteSelected')}
            </Button>
          </div>
        </div>
      )}

      <FinancialSupportFormDialog
        open={dialogOpen}
        entry={editing}
        saveError={saveError}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        isSaving={saveMutation.isPending}
      />

      <ConfirmationDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t(
          confirmDelete?.length === 1 ? 'financialSupport.delete.title' : 'financialSupport.delete.titleMany'
        )}
        description={deleteDescription()}
        confirmLabel={t('financialSupport.delete.confirm')}
        cancelLabel={t('financialSupport.form.cancel')}
        variant="destructive"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
      />
    </div>
  );
}
